

import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function HolidaySeason() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Houston Holiday Season Events & Celebrations",
    "description": "Discover Houston's magical winter holiday season with Christmas lights, Hanukkah celebrations, festive markets, and family-friendly events across the city.",
    "url": `${import.meta.env.VITE_SITE_URL}/holiday-season`,
    "about": {
      "@type": "Event",
      "name": "Houston Holiday Season",
      "location": {
        "@type": "City",
        "name": "Houston",
        "addressRegion": "Texas",
        "addressCountry": "US"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <SEO
        title="Houston Holiday Season Events 2024-2025 | Christmas, Hanukkah & Winter Celebrations"
        description="Experience Houston's magical winter holiday season with Christmas lights, Hanukkah celebrations, festive markets, ice skating, and family-friendly events across the city. Your complete guide to Houston holiday festivities."
        keywords="Houston Christmas events, Houston Hanukkah celebrations, Houston holiday lights, Houston winter festivals, Houston holiday markets, Houston ice skating, Houston holiday season, Christmas in Houston"
        ogTitle="Houston Holiday Season Events 2024-2025 | Eric Javidi Guide"
        ogDescription="Experience Houston's magical winter holiday season with Christmas lights, Hanukkah celebrations, festive markets, and family-friendly events across the city."
        ogType="website"
        ogUrl={`${import.meta.env.VITE_SITE_URL}/holiday-season`}
        twitterTitle="Houston Holiday Season Events 2024-2025"
        twitterDescription="Experience Houston's magical winter holiday season with Christmas lights, Hanukkah celebrations, festive markets, and family-friendly events."
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-neutral-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-semibold text-white">
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Home
                </Link>
                <Link to="/about" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  About
                </Link>
                <Link to="/guides" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Guides
                </Link>
                <Link to="/holiday-season" className="text-white hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Holiday Season
                </Link>
                <Link to="/fall-festivals" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Fall Festivals
                </Link>
                <Link to="/resources" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Resources
                </Link>
                <Link to="/contact" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-neutral-300 hover:text-white">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/44954f0e150701141fc4f23aed22d961.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/60"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <i className="ri-snowflake-line text-5xl text-blue-200 mr-4"></i>
              <h1 className="text-4xl lg:text-6xl font-bold text-white">
                Houston Holiday Season
              </h1>
            </div>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8">
              Experience the magic of winter in Houston with festive lights, cultural celebrations, holiday markets, and family-friendly events throughout the season.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <i className="ri-calendar-line text-white mr-2"></i>
                <span className="text-white font-medium">November - January</span>
              </div>
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <i className="ri-map-pin-line text-white mr-2"></i>
                <span className="text-white font-medium">Citywide Events</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-6">
            Celebrate the Holidays in Houston
          </h2>
          <p className="text-lg text-neutral-600 leading-relaxed mb-6">
            Houston transforms into a winter wonderland during the holiday season, offering spectacular light displays, diverse cultural celebrations, festive markets, and memorable experiences for all ages. From traditional Christmas festivities to Hanukkah celebrations and New Year's events, Houston's holiday season reflects the city's rich cultural diversity and warm community spirit.
          </p>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Whether you're seeking dazzling light shows, authentic cultural traditions, family-friendly activities, or festive dining experiences, Houston's holiday season has something magical for everyone.
          </p>
        </div>
      </section>

      {/* Holiday Events Grid */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-4">
              Featured Holiday Events
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Discover Houston's most beloved winter celebrations and create lasting holiday memories
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Event Card 1 - A Christmas Carol */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/1" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Alley%20Theatre%20Houston%20Christmas%20Carol%20performance%20with%20Victorian%20era%20costumes%2C%20dramatic%20stage%20lighting%2C%20classic%20theatrical%20production%2C%20festive%20holiday%20atmosphere%2C%20professional%20theatre%20setting%20with%20ornate%20stage%20design&width=400&height=350&seq=christmas-carol&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    CLASSIC
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-theatre-line text-2xl text-red-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    A Christmas Carol
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Experience the timeless holiday classic at Alley Theatre's Hubbard Theatre. A beloved tradition featuring spectacular staging and heartwarming performances.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Nov. 16 – Dec. 28, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Alley Theatre, Downtown</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-ticket-line mr-2"></i>
                    <span>From $36 (varies by date)</span>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-blue-800">
                    <strong>Tip:</strong> Book early for best dates. Note: Children under 6 not admitted. Uses strobe/flashing lights.
                  </p>
                </div>
                <Link
                  to="/holiday-detail/1"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 2 - White Christmas */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/2" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Theatre%20Under%20The%20Stars%20Houston%20White%20Christmas%20musical%20performance%20with%20dancers%20in%20festive%20costumes%2C%20snow%20falling%20on%20stage%2C%20classic%20holiday%20musical%20production%2C%20Hobby%20Center%20elegant%20theatre%20interior%20with%20red%20velvet%20seats&width=400&height=350&seq=white-christmas&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    MUSICAL
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-music-line text-2xl text-blue-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Irving Berlin's White Christmas
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Theatre Under The Stars presents this beloved musical at the Hobby Center. Features a spectacular snow finale that brings the magic to life.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Dec. 9 – 24, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Hobby Center, Downtown</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Family-friendly for all ages</span>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-blue-800">
                    <strong>Tip:</strong> Arrive early for parking. The snow finale is a highlight - dress for possible audience snow effect!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/2"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 3 - Elf in Concert */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/3" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Symphony%20Jones%20Hall%20concert%20performance%20with%20full%20orchestra%20on%20stage%2C%20elegant%20concert%20hall%20interior%20with%20ornate%20architecture%2C%20holiday%20concert%20atmosphere%2C%20professional%20musicians%20performing%20festive%20music&width=400&height=350&seq=elf-concert&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    CONCERT
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-film-line text-2xl text-green-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Elf in Concert
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Houston Symphony performs the beloved holiday film live at Jones Hall. Watch the movie on the big screen while the orchestra plays the score live.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Dec. 19 – 21, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Jones Hall, Downtown</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Perfect for families</span>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-green-800">
                    <strong>Tip:</strong> Arrive early for holiday crowds. Check seat location for best viewing experience with children.
                  </p>
                </div>
                <Link
                  to="/holiday-detail/3"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 4 - The Nutcracker */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/4" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Ballet%20Nutcracker%20performance%20with%20ballerinas%20in%20elegant%20tutus%2C%20Sugar%20Plum%20Fairy%20scene%2C%20Wortham%20Theater%20Center%20grand%20stage%2C%20classical%20ballet%20production%20with%20elaborate%20costumes%20and%20magical%20holiday%20staging&width=400&height=350&seq=nutcracker&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-purple-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    BALLET
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-user-star-line text-2xl text-purple-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    The Nutcracker
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Houston Ballet's stunning production of this holiday classic at Wortham Theater Center. A magical experience with world-class dancers and elaborate staging.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Nov. 28 – Dec. 28, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Wortham Theater Center</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-ticket-line mr-2"></i>
                    <span>From $60+</span>
                  </div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-purple-800">
                    <strong>Tip:</strong> ~2 hour runtime. Pick orchestra or first mezzanine seats for best views. Consider matinees for families.
                  </p>
                </div>
                <Link
                  to="/holiday-detail/4"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 5 - Nutcracker Market */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/5" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Ballet%20Nutcracker%20Market%20at%20NRG%20Park%20with%20festive%20vendor%20booths%2C%20holiday%20shopping%20displays%2C%20families%20browsing%20unique%20gifts%20and%20decorations%2C%20elegant%20market%20atmosphere%20with%20Christmas%20decorations%20and%20merchandise%20displays&width=400&height=350&seq=nutcracker-market&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    SHOPPING
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-shopping-bag-3-line text-2xl text-red-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Nutcracker Market®
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Houston Ballet's legendary holiday market at NRG Park. Shop unique gifts from hundreds of vendors while supporting ballet scholarships and academy programs.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Nov. 13 – 16, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>NRG Park</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-ticket-line mr-2"></i>
                    <span>$20 GA / $60 Early Bird (4 days)</span>
                  </div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Tip:</strong> Arrive early for best selection. Wear comfortable shoes. Kids 2 &amp; under free. Funds support ballet education!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/5"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Holiday Events Grid Continued */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Event Card 6 - Home for the Holidays */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/6" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Old%20Town%20Spring%20Texas%20Christmas%20holiday%20decorations%20with%20festive%20lights%2C%20charming%20historic%20buildings%2C%20families%20shopping%20at%20holiday%20market%2C%20vintage%20small%20town%20atmosphere%2C%20warm%20evening%20glow%2C%20Christmas%20tree%20lighting%20ceremony&width=400&height=350&seq=old-town-spring&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    CHARMING
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-home-heart-line text-2xl text-green-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Home for the Holidays
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Experience small-town holiday charm in Old Town Spring with festive lights, tree lighting ceremony, photos with Santa, and unique shopping at local boutiques.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Nov. 22 – Dec. 21 (Weekends)</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Old Town Spring, TX</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Family-friendly</span>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-green-800">
                    <strong>Tip:</strong> Arrive early for tree lighting on Nov 22. Plan travel time from Houston - parking fills on busy weekends!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/6"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 7 - Zoo Lights */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/7" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Zoo%20Lights%20Christmas%20display%20with%20colorful%20illuminated%20animal%20sculptures%2C%20families%20walking%20through%20magical%20light%20displays%2C%20festive%20holiday%20atmosphere%2C%20twinkling%20lights%20on%20trees%2C%20winter%20evening%20celebration&width=400&height=350&seq=zoo-lights-main&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    POPULAR
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-lightbulb-flash-line text-2xl text-yellow-500 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Zoo Lights
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Experience over 2 million twinkling lights transforming Houston Zoo into a magical winter wonderland with illuminated animal sculptures and immersive festive displays.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Nov. 21, 2025 – Jan. 4, 2026</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Houston Zoo</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Excellent for families</span>
                  </div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-yellow-800">
                    <strong>Tip:</strong> Dress in layers for evening. Arrive early to avoid congestion. Great photo opportunities!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/7"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 8 - Mayor's Holiday Spectacular */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/8" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20downtown%20Christmas%20tree%20lighting%20ceremony%20with%20large%20decorated%20tree%2C%20crowds%20gathered%20for%20celebration%2C%20festive%20downtown%20atmosphere%2C%20holiday%20lights%20illuminating%20building%2C%20community%20celebration%20event%2C%20mayor%20speaking%20at%20podium&width=400&height=350&seq=mayors-spectacular&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    TRADITION
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-government-line text-2xl text-red-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Deck the Hall: Mayor's Holiday Spectacular
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Join Houston's official holiday celebration with the Mayor's tree lighting ceremony, outdoor concert, and festive entertainment in the heart of downtown.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Dec. 6, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Downtown Houston</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Good for families</span>
                  </div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Tip:</strong> Arrive early for best spots. Outdoor event - bring jackets and layers. Expect big crowds!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/8"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 9 - Breakfast with Santa */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/9" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Downtown%20Aquarium%20Houston%20Christmas%20breakfast%20with%20Santa%20Claus%2C%20children%20meeting%20Santa%2C%20festive%20holiday%20buffet%2C%20families%20dining%20together%2C%20aquarium%20fish%20tanks%20in%20background%2C%20magical%20holiday%20atmosphere&width=400&height=350&seq=breakfast-santa&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    SPECIAL
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-restaurant-2-line text-2xl text-blue-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Breakfast with Santa
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Create magical memories at Downtown Aquarium with a festive breakfast buffet, photos with Santa, and underwater views of marine life.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Dec. 6, 13, 14, 20–24, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Downtown Aquarium</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-ticket-line mr-2"></i>
                    <span>Reservations required</span>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-blue-800">
                    <strong>Tip:</strong> Reserve early for specific dates. Arrive early for buffet and photos. Plan downtown parking ahead!
                  </p>
                </div>
                <Link
                  to="/holiday-detail/9"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Event Card 10 - Celebrating Kwanzaa */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <Link to="/holiday-detail/10" className="cursor-pointer">
                <div 
                  className="h-56 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Children%20Museum%20Houston%20Kwanzaa%20celebration%20with%20kids%20participating%20in%20African%20drumming%20circle%2C%20colorful%20traditional%20decorations%2C%20children%20doing%20art%20workshop%2C%20cultural%20heritage%20celebration%2C%20educational%20family%20event&width=400&height=350&seq=kwanzaa-celebration&orientation=landscape')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-purple-600 text-white px-3 py-1 text-xs font-bold rounded-full">
                    CULTURAL
                  </div>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <i className="ri-palette-line text-2xl text-purple-600 mr-2"></i>
                  <h3 className="text-xl font-bold text-neutral-800">
                    Celebrating Kwanzaa
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  Children's Museum Houston hosts an educational and interactive Kwanzaa celebration with art workshops, traditional drumming circle, and cultural activities for kids.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Dec. 26, 2025</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Children's Museum Houston</span>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-group-line mr-2"></i>
                    <span>Kids focused</span>
                  </div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-purple-800">
                    <strong>Tip:</strong> Great post-Christmas outing! Check museum admission ahead. Bring comfortable clothes for activities.
                  </p>
                </div>
                <Link
                  to="/holiday-detail/10"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Removed old placeholder cards - keeping only the new 5 events */}
          </div>
        </div>
      </section>

      {/* Holiday Activities Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-12 text-center">
            Holiday Activities &amp; Experiences
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                Holiday Shopping
              </h3>
              <p className="text-sm text-neutral-600">
                Unique gifts at festive markets and boutiques
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-music-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                Holiday Concerts
              </h3>
              <p className="text-sm text-neutral-600">
                Seasonal performances and caroling events
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-restaurant-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                Festive Dining
              </h3>
              <p className="text-sm text-neutral-600">
                Special holiday menus and themed restaurants
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-camera-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                Photo Opportunities
              </h3>
              <p className="text-sm text-neutral-600">
                Picture-perfect holiday displays and decorations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-red-50 to-green-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-8 text-center">
            Holiday Season Tips
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-ticket-line text-xl text-blue-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                    Book Early
                  </h3>
                  <p className="text-neutral-600">
                    Popular events like Zoo Lights and ice skating sell out quickly. Purchase tickets in advance to secure your preferred dates and times.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-temp-cold-line text-xl text-green-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                    Dress for Houston Weather
                  </h3>
                  <p className="text-neutral-600">
                    Houston winters are mild but can be unpredictable. Layer clothing and bring a light jacket for evening events, even if days are warm.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <i className="ri-parking-line text-xl text-purple-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                    Plan Parking Ahead
                  </h3>
                  <p className="text-neutral-600">
                    Downtown events can have limited parking. Arrive early or use ride-share services to avoid parking hassles during peak times.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <i className="ri-group-line text-xl text-orange-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                    Family-Friendly Focus
                  </h3>
                  <p className="text-neutral-600">
                    Most Houston holiday events are designed for families. Check event details for age recommendations and accessibility information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <i className="ri-gift-2-line text-6xl text-white"></i>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Make This Holiday Season Memorable
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Explore Eric's complete guides to Houston's neighborhoods, parks, and year-round attractions to plan your perfect holiday season.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/guides"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              Browse All Guides
              <i className="ri-map-line ml-2"></i>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get In Touch
              <i className="ri-mail-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold mb-4">Eric Javidi</h3>
              <p className="text-neutral-300 mb-4">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.youtube.com/@EricJavidi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-red-400 transition-colors"
                  aria-label="YouTube Channel"
                >
                  <i className="ri-youtube-fill text-2xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/ericjavidihouston/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-blue-400 transition-colors"
                  aria-label="Facebook Page"
                >
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-neutral-300 hover:text-white">About</Link></li>
                <li><Link to="/guides" className="text-neutral-300 hover:text-white">Guides</Link></li>
                <li><Link to="/holiday-season" className="text-neutral-300 hover:text-white">Holiday Season</Link></li>
                <li><Link to="/fall-festivals" className="text-neutral-300 hover:text-white">Fall Festivals</Link></li>
                <li><Link to="/resources" className="text-neutral-300 hover:text-white">Resources</Link></li>
                <li><Link to="/contact" className="text-neutral-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.youtube.com/@EricJavidi" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">YouTube Channel</a></li>
                <li><a href="https://www.facebook.com/ericjavidihouston/" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">Facebook Page</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="text-neutral-300 hover:text-white">Website Builder</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-neutral-700 mt-8 pt-8 text-center">
            <p className="text-neutral-400">
              © 2024 Steven Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

